import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, Loader2, Plus, Save, Trash2,
  Folder, CheckCircle2,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminProductCategories() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/product-categories");
      setItems(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/product-categories", { name });
      setNewName("");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(
      `Delete category "${cat.name}"? Products in this category will move back to "Uncategorised".`,
    )) return;
    setError("");
    try {
      await api.delete(`/admin/product-categories/${cat.id}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (idx, delta) => {
    if (!items) return;
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    try {
      await api.post("/admin/product-categories/reorder", {
        ids: next.map((c) => c.id),
      });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-product-categories"
    >
      <Header />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin/products"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to products
        </RouterLink>

        <div className="mt-6 mb-8">
          <div className="flex items-center gap-2 text-brand-accent">
            <Folder size={16} strokeWidth={2.5} />
            <span className="label-kicker">Product categories</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Organise your shop.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            Group products into categories — Shirts, Books, DVDs, Memorabilia,
            whatever you sell. Public visitors see these as filter tabs above
            the product grid. Drag to reorder; renaming updates the tab
            everywhere.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="product-category-error"
          >
            {error}
          </p>
        ) : null}

        <form
          onSubmit={onCreate}
          className="mb-8 rounded-md border border-black/10 bg-[#F8FAFC] p-5"
          data-testid="product-category-create-form"
        >
          <p className="label-kicker text-[var(--brand-body)]">Add a new category</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[200px]">
              <span className="label-kicker text-[var(--brand-body)]">Name</span>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={80}
                placeholder="Shirts"
                data-testid="product-category-new-name"
                className="mt-1 w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </label>
            <button
              type="submit"
              disabled={!newName.trim() || creating}
              data-testid="product-category-create-submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add category
            </button>
          </div>
        </form>

        {items === null ? (
          <p className="text-sm text-[var(--brand-body)]">Loading…</p>
        ) : items.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="product-category-empty"
          >
            <p className="font-display text-lg font-bold">No categories yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Add your first category above (e.g. &ldquo;Shirts&rdquo;).
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="product-category-list">
            {items.map((c, idx) => (
              <CategoryRow
                key={c.id}
                category={c}
                onSaved={refresh}
                onDelete={() => remove(c)}
                onMoveUp={idx === 0 ? null : () => move(idx, -1)}
                onMoveDown={idx === items.length - 1 ? null : () => move(idx, 1)}
                setOuterError={setError}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CategoryRow({ category, onSaved, onDelete, onMoveUp, onMoveDown, setOuterError }) {
  const [name, setName] = useState(category.name);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setName(category.name); }, [category.name]);

  const dirty = name.trim() !== category.name && name.trim().length > 0;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    setOuterError("");
    try {
      await api.put(`/admin/product-categories/${category.id}`, { name: name.trim() });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await onSaved();
    } catch (e) {
      setOuterError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-md border border-black/10 bg-white p-4"
      data-testid={`product-category-row-${category.id}`}
    >
      <span
        className="rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-white"
        data-testid={`product-category-preview-${category.id}`}
      >
        {category.name}
      </span>
      <span className="font-mono text-xs text-[var(--brand-body)]">
        slug: <code>{category.slug}</code>
      </span>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        data-testid={`product-category-name-${category.id}`}
        className="ml-auto w-48 rounded-md border border-black/15 bg-white px-3 py-1.5 text-sm"
      />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          aria-label="Move up"
          data-testid={`product-category-${category.id}-up`}
          className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40"
        >
          <ArrowUp size={12} />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          aria-label="Move down"
          data-testid={`product-category-${category.id}-down`}
          className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40"
        >
          <ArrowDown size={12} />
        </button>
      </div>
      <button
        type="button"
        onClick={save}
        disabled={!dirty || saving}
        data-testid={`product-category-save-${category.id}`}
        className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-semibold text-white hover:bg-brand-accent disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        Save
      </button>
      {savedFlash ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
          <CheckCircle2 size={12} /> Saved
        </span>
      ) : null}
      <button
        type="button"
        onClick={onDelete}
        data-testid={`product-category-delete-${category.id}`}
        className="inline-flex items-center gap-1 rounded-md border border-[#E63946]/40 px-2 py-1.5 text-xs text-[#E63946] hover:bg-[#E63946] hover:text-white"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}
